import React, { useState, useEffect } from 'react';


const MarketsTable = ({ tickers, homeUrl, exchangeLogos  }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;
  
  // Calculate total pages
  const totalPages = Math.ceil(tickers.length / entriesPerPage);
  
  // Get current page data
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = tickers.slice(indexOfFirstEntry, indexOfLastEntry);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  const formatAddress = (address) => {
    if (!address) return '';
    // Handle the extremely long malformed address case
    if (address.length > 100) return '...';
    if (address.length > 10) {
      return `${address.slice(0, 4)}...${address.slice(-4)}`;
    }
    return address;
  };

  MarketsTable.defaultProps = {
    exchangeLogos: {},
  };
  return (
    <div className="markets-table-container">
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Exchange</th>
              <th scope="col">Pair</th>
              <th scope="col">Price</th>
              <th scope="col">Spread</th>
              <th scope="col">+2% Depth</th>
              <th scope="col">-2% Depth</th>
              <th scope="col">24h Volume</th>
              <th scope="col">Volume %</th>
              <th scope="col">Last Updated</th>
              <th scope="col">Trust Score</th>
            </tr>
          </thead>
          
          <tbody>
            {currentEntries.length > 0 && currentEntries.map((ticker, index) => {
              const globalIndex = indexOfFirstEntry + index;
              const volumePercent = globalIndex === 0 ? 100 : 
                ((ticker.converted_volume?.usd || 0) / 
                 (tickers[0]?.converted_volume?.usd || 1)) * 100;
              
              const depth = (ticker.converted_volume?.usd || 0) * 0.02;
              
              return (
                <tr key={`${ticker.market?.identifier}-${globalIndex}`}>
                  <td>{globalIndex + 1}</td>
                  <td>
                    <div className="cryptonamebtn">
                      <div className="logoname">
                        <img
                          src={
                            exchangeLogos && exchangeLogos[ticker.market?.identifier]
                              ? exchangeLogos[ticker.market?.identifier]
                              : `${homeUrl}/assets/img/exchange-default.png`
                          }
                          alt={`${ticker.market?.name || 'Exchange'} logo`}
                          style={{ width: '20px', marginRight: '8px' }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `${homeUrl}/images/exchange-default.png`;
                          }}
                        />
                        <span>
                         {ticker.market?.name || 'Unknown'}
                        </span>
                      </div>
                      <div className="cextxt">CEX</div>
                    </div>
                  </td>
                  
                  <td>
                    <a href={ticker.trade_url} target="_blank" className="whitespace-nowrap">
                      {ticker.coin_id?.toUpperCase() || formatAddress(ticker.base)}/
                      {formatAddress(ticker.target)}
                      {ticker.trust_score && (
                        <span className="verified">
                          <i data-view-component="true" className="tw-ml-1 fas fa-arrow-up-right-from-square"></i>
                        </span>
                      )}
                    </a>
                  </td>
                  
                  <td>
                    ${ticker.last?.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }) || 'N/A'}
                  </td>
                  <td>
                    {ticker.bid_ask_spread_percentage?.toFixed(2) || '0.00'}%
                  </td>
                  <td>
                    ${depth.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </td>
                  <td>
                    ${depth.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </td>
                  <td>
                    ${(ticker.converted_volume?.usd || 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </td>
                  <td>
                    {volumePercent.toFixed(2)}%
                  </td>
                  <td>
                    {ticker.last_fetch_at 
                      ? new Date(ticker.last_fetch_at).toLocaleTimeString() 
                      : 'Recently'}
                  </td>
                  <td>
                    <span className={`trustscore ${ticker.trust_score ? 'green' : 'gray'}`}>
                      <i className="fas fa-circle"></i>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      <div className="pagination-container">
        <button 
          onClick={prevPage} 
          disabled={currentPage === 1}
          className="pagination-button"
        >
          &laquo; Previous
        </button>
        
        <div className="page-numbers">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNumber;
            if (totalPages <= 5) {
              pageNumber = i + 1;
            } else if (currentPage <= 3) {
              pageNumber = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNumber = totalPages - 4 + i;
            } else {
              pageNumber = currentPage - 2 + i;
            }
            
            return (
              <button
                key={pageNumber}
                onClick={() => paginate(pageNumber)}
                className={`pagination-button ${currentPage === pageNumber ? 'active' : ''}`}
              >
                {pageNumber}
              </button>
            );
          })}
          
          {totalPages > 5 && currentPage < totalPages - 2 && (
            <span className="page-ellipsis">...</span>
          )}
          
          {totalPages > 5 && currentPage < totalPages - 2 && (
            <button
              onClick={() => paginate(totalPages)}
              className={`pagination-button ${currentPage === totalPages ? 'active' : ''}`}
            >
              {totalPages}
            </button>
          )}
        </div>
        
        <button 
          onClick={nextPage} 
          disabled={currentPage === totalPages}
          className="pagination-button"
        >
          Next &raquo;
        </button>
        
        <div className="page-info">
          Showing {indexOfFirstEntry + 1}-{Math.min(indexOfLastEntry, tickers.length)} of {tickers.length} entries
        </div>
      </div>
    </div>
  );
};

export default MarketsTable;